/*jshint loopfunc: true*/

/*
  ------------------------------------------------------------

  Simple benchmark runner and results generator

    see: https://gist.github.com/1364172

  ------------------------------------------------------------

  Runs benchmarks and generates the results in a table.

  Setup benchmarks to run in an array of objects with two properties:

    name: a title for the table column of results
    numeric: boolean, used to decide what columns should be used to calculate averages
    formatter: (optional) a function that takes a number and returns a formmatted string, example: d3.format("5.1f")
    run: a function that is called to run the benchmark and call back with a value.
         It should accept a single argument, the callback to be called when the
         benchmark completes. It should pass the benchmark value to the callback.

  Start the benchmarks by passing the table element where the results are to
  be placed and an array of benchmarks to run.

  Example:

    var benchmarks_table = document.getElementById("benchmarks-table");

    var benchmarks_to_run = [
      {
        name: "molecules",
        run: function(done) {
          done(mol_number);
        }
      },
      {
        name: "100 Steps (steps/s)",
        run: function(done) {
          modelStop();
          var start = +Date.now();
          var i = -1;
          while (i++ < 100) {
            model.tick();
          }
          elapsed = Date.now() - start;
          done(d3.format("5.1f")(100/elapsed*1000));
        }
      },
    ];

    benchmark.run(benchmarks_table, benchmarks_to_run)

  You can optionally pass two additional arguments to the run method: start_callback, end_callback

    function run(benchmarks_table, benchmarks_to_run, start_callback, end_callback)

  These arguments are used when the last benchmark test is run using the browsers scheduling and re-painting mechanisms.

  For example this test runs a model un the browser and calculates actual frames per second combining the
  model, view, and browser scheduling and repaint operations.

    {
      name: "fps",
      numeric: true,
      formatter: d3.format("5.1f"),
      run: function(done) {
        // warmup
        model.start();
        setTimeout(function() {
          model.stop();
          var start = model.get('time');
          setTimeout(function() {
            // actual fps calculation
            model.start();
            setTimeout(function() {
              model.stop();
              var elapsedModelTime = model.get('time') - start;
              done( elapsedModelTime / (model.get('timeStepsPerTick') * model.get('timeStep')) / 2 );
            }, 2000);
          }, 100);
        }, 1000);
      }
    }

  Here's an example calling the benchmark.run method and passing in start_callback, end_callback functions:

    benchmark.run(document.getElementById("model-benchmark-results"), benchmarksToRun, function() {
      $runBenchmarksButton.attr('disabled', true);
    }, function() {
      $runBenchmarksButton.attr('disabled', false);
    });

  The "Run Benchmarks" button is disabled until the browser finishes running thelast queued test.

  The first five columns in the generated table consist of:

    browser, version, cpu/os, date, and commit

  These columns are followed by a column for each benchmark passed in.

  Subsequent calls to: benchmark.run(benchmarks_table, benchmarks_to_run) will
  add additional rows to the table.

  A special second row is created in the table which displays averages of all tests
  that generate numeric results.

  Here are some css styles for the table:

    table {
      font: 11px/24px Verdana, Arial, Helvetica, sans-serif;
      border-collapse: collapse; }
    th {
      padding: 0 1em;
      text-align: left; }
    td {
      border-top: 1px solid #cccccc;
      padding: 0 1em; }

*/

import browser_detect from './browser-detect';
var what_browser = browser_detect.what_browser,

  _isMobile = browser_detect.isMobile,
  _browser = browser_detect.what_browser(),

  average_row;

function renderToTable(benchmarks_table, benchmarksThatWereRun, results) {
  var i = 0,
    results_row,
    result,
    col_number = 0,
    col_numbers = {},
    title_row,
    title_cells,
    len,
    rows = benchmarks_table.getElementsByTagName("tr");

  benchmarks_table.style.display = "";

  function add_column(title) {
    var title_row = benchmarks_table.getElementsByTagName("tr")[0],
      cell = title_row.appendChild(document.createElement("th"));

    cell.innerHTML = title;
    col_numbers[title] = col_number++;
  }

  function add_row(num_cols) {
    num_cols = num_cols || 0;
    var tr = benchmarks_table.appendChild(document.createElement("tr")),
      i;

    for (i = 0; i < num_cols; i++) {
      tr.appendChild(document.createElement("td"));
    }
    return tr;
  }

  function add_result(name, content, row) {
    var cell;
    row = row || results_row;
    cell = row.getElementsByTagName("td")[col_numbers[name]];
    if (typeof content === "string" && content.slice(0, 1) === "<") {
      cell.innerHTML = content;
    } else {
      cell.textContent = content;
    }
  }

  function update_averages() {
    var i, j,
      b,
      row,
      num_rows = rows.length,
      cell,
      cell_index,
      average_elements = average_row.getElementsByTagName("td"),
      total,
      average,
      genericDecimalFormatter = d3.format("5.1f"),
      genericIntegerFormatter = d3.format("f");

    function isInteger(i) {
      return Math.floor(i) === i;
    }

    for (i = 0; i < benchmarksThatWereRun.length; i++) {
      b = benchmarksThatWereRun[i];
      cell_index = col_numbers[b.name];
      if (b.numeric === false) {
        row = rows[2];
        cell = row.getElementsByTagName("td")[cell_index];
        average_elements[cell_index].innerHTML = cell.innerHTML;
      } else {
        total = 0;
        for (j = 2; j < num_rows; j++) {
          row = rows[j];
          cell = row.getElementsByTagName("td")[cell_index];
          total += (+cell.textContent);
        }
        average = total / (num_rows - 2);
        if (b.formatter) {
          average = b.formatter(average);
        } else {
          if (isInteger(average)) {
            average = genericIntegerFormatter(total / (num_rows - 2));
          } else {
            average = genericDecimalFormatter(total / (num_rows - 2));
          }
        }
        average_elements[cell_index].textContent = average;
      }
    }
  }

  if (rows.length === 0) {
    add_row();
    add_column("browser");
    add_column("version");
    add_column("cpu/os");
    add_column("date");
    add_column("commit");
    add_column("branch");
    for (i = 0; i < benchmarksThatWereRun.length; i++) {
      add_column(benchmarksThatWereRun[i].name);
    }
    average_row = add_row(col_number);
    average_row.className = 'average';
  } else {
    title_row = rows[0];
    title_cells = title_row.getElementsByTagName("th");
    for (i = 0, len = title_cells.length; i < len; i++) {
      col_numbers[title_cells[i].innerHTML] = col_number++;
    }
  }

  results_row = add_row(col_number);
  results_row.className = 'sample';

  for (i = 0; i < 6; i++) {
    result = results[i];
    add_result(result[0], result[1]);
    add_result(result[0], result[1], average_row);
  }

  for (i = 6; i < results.length; i++) {
    result = results[i];
    add_result(result[0], result[1]);
  }
  update_averages();
}

function bench(benchmarks_to_run, resultsCallback, start_callback, end_callback) {
  var bencharks_queue = benchmarks_to_run.slice(),
    results = [],
    browser_info = what_browser(),
    formatter = d3.time.format("%Y-%m-%d %H:%M"),
    commit_link;

  results.push(["browser", browser_info.browser]);
  results.push(["version", browser_info.version]);
  results.push(["cpu/os", browser_info.oscpu]);
  results.push(["date", formatter(new Date())]);

  commit_link = "<a href='" + Lab.version.repo.commit.url + "' class='opens-in-new-window' target='_blank'>" + Lab.version.repo.commit.short_sha + "</a>";
  if (Lab.version.repo.dirty) {
    commit_link += " <i>dirty</i>";
  }
  results.push(["commit", commit_link]);
  results.push(["branch", Lab.version.repo.branch]);

  if (start_callback) start_callback();

  runBenchmark(bencharks_queue.shift());

  function runBenchmark(b) {
    b.run(doneCallback);

    function doneCallback(result) {
      if (b.formatter) {
        results.push([b.name, b.formatter(result)]);
      } else {
        results.push([b.name, result]);
      }

      if (bencharks_queue.length > 0) {
        runBenchmark(bencharks_queue.shift());
      } else {
        if (end_callback) end_callback();
        if (resultsCallback) resultsCallback(results);
      }
    }
  }

  return results;
}

function run(benchmarks_to_run, benchmarks_table, resultsCallback, start_callback, end_callback) {
  var results;
  bench(benchmarks_to_run, function(results) {
    renderToTable(benchmarks_table, benchmarks_to_run, results);
    resultsCallback(results);
  }, start_callback, end_callback);
  return results;
}

// Return Public API.
export default {
  /**
   * Browser description.
   */
  get browser() {
    return _browser;
  },
  /**
   * Triggers recalculation of browser description and returns the result.
   * Depreciated, use .browser property instead.
   */
  what_browser: function() {
    _browser = what_browser();
    return _browser;
  },
  get isMobile() {
    return _isMobile;
  },
  // run benchmarks, add row to table, update averages row
  run: function(benchmarks_to_run, benchmarks_table, resultsCallback, start_callback, end_callback) {
    run(benchmarks_to_run, benchmarks_table, resultsCallback, start_callback, end_callback);
  },
  // run benchmarks, return results in object
  bench: function(benchmarks_to_run, resultsCallback, start_callback, end_callback) {
    return bench(benchmarks_to_run, resultsCallback, start_callback, end_callback);
  },
  // run benchmarks, add row to table, update averages row
  renderToTable: function(benchmarks_table, benchmarksThatWereRun, results) {
    renderToTable(benchmarks_table, benchmarksThatWereRun, results);
  }
};
